/**
 * Playful Messages Collection
 * Curated messages for the playful toast notification system
 */

export type MessageCategory =
    | 'sales_wisdom'
    | 'coffee_break'
    | 'motivation'
    | 'humor'
    | 'wellness';

export type GradientTheme =
    | 'sunrise'
    | 'ocean'
    | 'blossom'
    | 'forest'
    | 'cosmos';

export interface PlayfulMessage {
    id: string;
    text: string;
    emoji: string;
    category: MessageCategory;
    theme: GradientTheme;
}

// Gradient CSS classes for each theme
export const gradientThemes: Record<GradientTheme, string> = {
    sunrise: 'from-amber-400 via-orange-500 to-pink-500',
    ocean: 'from-cyan-400 via-blue-500 to-indigo-600',
    blossom: 'from-pink-400 via-rose-500 to-purple-600',
    forest: 'from-emerald-400 via-green-500 to-teal-600',
    cosmos: 'from-violet-400 via-purple-500 to-indigo-600',
};

// Category icons for display
export const categoryIcons: Record<MessageCategory, string> = {
    sales_wisdom: '💡',
    coffee_break: '☕',
    motivation: '🔥',
    humor: '😄',
    wellness: '🧘',
};

export const playfulMessages: PlayfulMessage[] = [
    // Sales Wisdom
    { id: 'sw1', text: "Fortune favors the follow-up!", emoji: '🎯', category: 'sales_wisdom', theme: 'ocean' },
    { id: 'sw2', text: "Every 'no' brings you closer to 'yes'!", emoji: '💪', category: 'sales_wisdom', theme: 'cosmos' },
    { id: 'sw3', text: "Listen twice, pitch once.", emoji: '👂', category: 'sales_wisdom', theme: 'forest' },
    { id: 'sw4', text: "The best time to call? Right now!", emoji: '📞', category: 'sales_wisdom', theme: 'sunrise' },
    { id: 'sw5', text: "Objections are just questions in disguise.", emoji: '🔮', category: 'sales_wisdom', theme: 'blossom' },
    { id: 'sw6', text: "Your network is your net worth!", emoji: '🕸️', category: 'sales_wisdom', theme: 'ocean' },
    { id: 'sw7', text: "Consistency beats intensity. Keep going!", emoji: '🏃', category: 'sales_wisdom', theme: 'forest' },
    { id: 'sw8', text: "The money is in the follow-up!", emoji: '💰', category: 'sales_wisdom', theme: 'sunrise' },
    { id: 'sw9', text: "Sell the problem you solve, not the product.", emoji: '🧩', category: 'sales_wisdom', theme: 'cosmos' },
    { id: 'sw10', text: "Be curious, not pushy.", emoji: '🤔', category: 'sales_wisdom', theme: 'blossom' },

    // Coffee Break
    { id: 'cb1', text: "Time for a coffee break! Caffeine fuels deals!", emoji: '☕', category: 'coffee_break', theme: 'sunrise' },
    { id: 'cb2', text: "Espresso yourself! Take a moment.", emoji: '🫖', category: 'coffee_break', theme: 'blossom' },
    { id: 'cb3', text: "Coffee: because adulting is hard.", emoji: '😴', category: 'coffee_break', theme: 'cosmos' },
    { id: 'cb4', text: "Brew-tiful day for a quick break!", emoji: '🌟', category: 'coffee_break', theme: 'sunrise' },
    { id: 'cb5', text: "A latte good things await after a break!", emoji: '🥛', category: 'coffee_break', theme: 'forest' },
    { id: 'cb6', text: "Take five! Your brain will thank you.", emoji: '🧠', category: 'coffee_break', theme: 'ocean' },
    { id: 'cb7', text: "Recharge mode: activated!", emoji: '🔋', category: 'coffee_break', theme: 'cosmos' },
    { id: 'cb8', text: "Step away, come back stronger!", emoji: '💫', category: 'coffee_break', theme: 'blossom' },

    // Motivation
    { id: 'mt1', text: "You're crushing it today!", emoji: '🏆', category: 'motivation', theme: 'sunrise' },
    { id: 'mt2', text: "Champions train, losers complain. You're a champion!", emoji: '👑', category: 'motivation', theme: 'cosmos' },
    { id: 'mt3', text: "Success is just around the corner!", emoji: '🚀', category: 'motivation', theme: 'ocean' },
    { id: 'mt4', text: "Your persistence is paying off!", emoji: '⭐', category: 'motivation', theme: 'sunrise' },
    { id: 'mt5', text: "Excellence is a habit. You've got this!", emoji: '💎', category: 'motivation', theme: 'blossom' },
    { id: 'mt6', text: "Dream big, work hard, stay focused!", emoji: '🎯', category: 'motivation', theme: 'forest' },
    { id: 'mt7', text: "You're one call away from a breakthrough!", emoji: '📱', category: 'motivation', theme: 'ocean' },
    { id: 'mt8', text: "Progress, not perfection!", emoji: '📈', category: 'motivation', theme: 'cosmos' },
    { id: 'mt9', text: "Today's effort is tomorrow's reward!", emoji: '🌅', category: 'motivation', theme: 'sunrise' },
    { id: 'mt10', text: "Believe in your hustle!", emoji: '✨', category: 'motivation', theme: 'blossom' },

    // Humor
    { id: 'hm1', text: "Plot twist: The client actually reads your emails!", emoji: '📧', category: 'humor', theme: 'blossom' },
    { id: 'hm2', text: "Your quota called. It misses you.", emoji: '📞', category: 'humor', theme: 'cosmos' },
    { id: 'hm3', text: "Sales tip: Be yourself. Unless you can be Batman.", emoji: '🦇', category: 'humor', theme: 'ocean' },
    { id: 'hm4', text: "Pipeline? More like wine-line after this week!", emoji: '🍷', category: 'humor', theme: 'blossom' },
    { id: 'hm5', text: "May your inbox be light and your deals be many!", emoji: '🙏', category: 'humor', theme: 'sunrise' },
    { id: 'hm6', text: "CRM stands for 'Caffeine Requirements Met', right?", emoji: '😅', category: 'humor', theme: 'forest' },
    { id: 'hm7', text: "Is it a meeting or could this be an email? You decide.", emoji: '🤷', category: 'humor', theme: 'cosmos' },
    { id: 'hm8', text: "Rejection is just the universe's 'not yet'!", emoji: '🌌', category: 'humor', theme: 'ocean' },

    // Wellness
    { id: 'wl1', text: "Stand up and stretch! Your back will thank you.", emoji: '🧘', category: 'wellness', theme: 'forest' },
    { id: 'wl2', text: "Hydration check! Water = Winning!", emoji: '💧', category: 'wellness', theme: 'ocean' },
    { id: 'wl3', text: "Take three deep breaths. You deserve calm.", emoji: '🌬️', category: 'wellness', theme: 'blossom' },
    { id: 'wl4', text: "Roll those shoulders! Release the tension.", emoji: '🔄', category: 'wellness', theme: 'forest' },
    { id: 'wl5', text: "Look away from the screen. Rest your eyes.", emoji: '👀', category: 'wellness', theme: 'cosmos' },
    { id: 'wl6', text: "Smile! It boosts your mood and your calls.", emoji: '😊', category: 'wellness', theme: 'sunrise' },
    { id: 'wl7', text: "Quick walk? Fresh air = fresh ideas!", emoji: '🚶', category: 'wellness', theme: 'forest' },
    { id: 'wl8', text: "Posture check! Sit tall, sell tall.", emoji: '🪑', category: 'wellness', theme: 'ocean' },
    { id: 'wl9', text: "Gratitude moment: What's one win today?", emoji: '🙌', category: 'wellness', theme: 'sunrise' },
    { id: 'wl10', text: "Mental health matters. How are you really feeling?", emoji: '💚', category: 'wellness', theme: 'blossom' },

    // Additional Sales Wisdom
    { id: 'sw11', text: "People buy from people they trust. Build that trust!", emoji: '🤝', category: 'sales_wisdom', theme: 'ocean' },
    { id: 'sw12', text: "Ask better questions, get better answers!", emoji: '❓', category: 'sales_wisdom', theme: 'cosmos' },
    { id: 'sw13', text: "Your attitude determines your altitude!", emoji: '🎈', category: 'sales_wisdom', theme: 'sunrise' },
    { id: 'sw14', text: "Rejection is redirection to something better!", emoji: '🧭', category: 'sales_wisdom', theme: 'forest' },
    { id: 'sw15', text: "Master the art of storytelling!", emoji: '📖', category: 'sales_wisdom', theme: 'blossom' },
    { id: 'sw16', text: "Value first, price second!", emoji: '💎', category: 'sales_wisdom', theme: 'ocean' },
    { id: 'sw17', text: "Silence is a powerful sales tool!", emoji: '🤫', category: 'sales_wisdom', theme: 'cosmos' },
    { id: 'sw18', text: "Your energy is contagious. Make it positive!", emoji: '⚡', category: 'sales_wisdom', theme: 'sunrise' },

    // Additional Motivation
    { id: 'mt11', text: "Small wins lead to big victories!", emoji: '🎊', category: 'motivation', theme: 'blossom' },
    { id: 'mt12', text: "You're building something amazing!", emoji: '🏗️', category: 'motivation', theme: 'forest' },
    { id: 'mt13', text: "Every expert was once a beginner!", emoji: '🌱', category: 'motivation', theme: 'forest' },
    { id: 'mt14', text: "Your future self will thank you!", emoji: '⏰', category: 'motivation', theme: 'cosmos' },
    { id: 'mt15', text: "Momentum is building. Keep pushing!", emoji: '🌊', category: 'motivation', theme: 'ocean' },
    { id: 'mt16', text: "You're making a difference!", emoji: '🌟', category: 'motivation', theme: 'sunrise' },
    { id: 'mt17', text: "Greatness is in the details!", emoji: '🔍', category: 'motivation', theme: 'blossom' },
    { id: 'mt18', text: "Your dedication is inspiring!", emoji: '💫', category: 'motivation', theme: 'cosmos' },

    // Additional Humor
    { id: 'hm9', text: "Coffee: turning 'I can't' into 'I did' since forever!", emoji: '☕', category: 'humor', theme: 'sunrise' },
    { id: 'hm10', text: "Sales forecast: 100% chance of closing deals!", emoji: '🌤️', category: 'humor', theme: 'ocean' },
    { id: 'hm11', text: "I'm not procrastinating, I'm strategically waiting!", emoji: '⏳', category: 'humor', theme: 'blossom' },
    { id: 'hm12', text: "My CRM and I have a love-hate relationship!", emoji: '💔', category: 'humor', theme: 'cosmos' },
    { id: 'hm13', text: "Sales tip: Ctrl+Z doesn't work in real life!", emoji: '⌨️', category: 'humor', theme: 'forest' },
    { id: 'hm14', text: "Warning: May spontaneously talk about deals!", emoji: '⚠️', category: 'humor', theme: 'sunrise' },
    { id: 'hm15', text: "I put the 'pro' in procrastination... wait, no!", emoji: '🤦', category: 'humor', theme: 'blossom' },

    // Additional Coffee Break
    { id: 'cb9', text: "Brain.exe has stopped working. Coffee required!", emoji: '🖥️', category: 'coffee_break', theme: 'cosmos' },
    { id: 'cb10', text: "Productivity tip: Take breaks to stay sharp!", emoji: '🎯', category: 'coffee_break', theme: 'ocean' },
    { id: 'cb11', text: "Stretch those legs! Movement = Energy!", emoji: '🦵', category: 'coffee_break', theme: 'forest' },
    { id: 'cb12', text: "Quick snack? Fuel your success!", emoji: '🍎', category: 'coffee_break', theme: 'sunrise' },

    // Additional Wellness
    { id: 'wl11', text: "Blink more! Your eyes need love too!", emoji: '👁️', category: 'wellness', theme: 'ocean' },
    { id: 'wl12', text: "Desk yoga time! Twist and release!", emoji: '🤸', category: 'wellness', theme: 'forest' },
    { id: 'wl13', text: "Power nap? 20 minutes can work wonders!", emoji: '😴', category: 'wellness', theme: 'blossom' },
    { id: 'wl14', text: "Healthy snack = Healthy mind!", emoji: '🥗', category: 'wellness', theme: 'forest' },
    { id: 'wl15', text: "End of day reflection: What went well?", emoji: '🌅', category: 'wellness', theme: 'sunrise' },
];

/**
 * Get a random message, optionally filtered by category
 */
export function getRandomMessage(categories?: MessageCategory[]): PlayfulMessage {
    let availableMessages = playfulMessages;

    if (categories && categories.length > 0) {
        availableMessages = playfulMessages.filter(m => categories.includes(m.category));
    }

    const randomIndex = Math.floor(Math.random() * availableMessages.length);
    return availableMessages[randomIndex];
}

/**
 * Get messages by category
 */
export function getMessagesByCategory(category: MessageCategory): PlayfulMessage[] {
    return playfulMessages.filter(m => m.category === category);
}

export default playfulMessages;
